import * as THREE from '../../libs/three.js/build/three.module.js';
import { TextSprite } from '../TextSprite.js';

const { Float32BufferAttribute } = THREE;

export class Volume extends THREE.Object3D {
  constructor(args = {}) {
    super();

    if (this.constructor.name === 'Volume') {
      console.warn(
        "Can't create object of class Volume directly. Use classes BoxVolume or SphereVolume instead."
      );
    }


    this._clip = args.clip || false;
    this._visible = true;
    this.showVolumeLabel = true;
    this._modifiable = args.modifiable || true;
    this._folder = null;

    this.label = new TextSprite('0');
    this.label.setBorderColor({ r: 0, g: 255, b: 0, a: 0.0 });
    this.label.setBackgroundColor({ r: 0, g: 255, b: 0, a: 0.0 });
    this.label.material.depthTest = false;
    this.label.material.depthWrite = false;
    this.label.material.transparent = true;
    this.label.position.y -= 0.5;
    this.add(this.label);

    this.label.updateMatrixWorld = () => {
      let volumeWorldPos = new THREE.Vector3();
      volumeWorldPos.setFromMatrixPosition(this.matrixWorld);
      this.label.position.copy(volumeWorldPos);
      this.label.updateMatrix();
      this.label.matrixWorld.copy(this.label.matrix);
      this.label.matrixWorldNeedsUpdate = false;

      for (let i = 0, l = this.label.children.length; i < l; i++) {
        this.label.children[i].updateMatrixWorld(true);
      }
    };

    {
      // event listeners
      this.addEventListener('select', (e) => { });
      this.addEventListener('deselect', (e) => { });
    }
  }

  get folder() {
    return this._folder;
  }

  set folder(folder) {
    this._folder = folder;
  }

  get visible() {
    return this._visible;
  }

  set visible(value) {
    if (this._visible !== value) {
      this._visible = value;

      this.dispatchEvent({ type: 'visibility_changed', object: this });
    }
  }

  getVolume() {
    console.warn('override this in subclass');
  }

  update() { }

  raycast(raycaster, intersects) { }

  get clip() {
    return this._clip;
  }

  set clip(value) {
    if (this._clip !== value) {
      this._clip = value;

      this.update();

      this.dispatchEvent({
        type: 'clip_changed',
        object: this,
      });
    }
  }

  get modifieable() {
    return this._modifiable;
  }

  set modifieable(value) {
    this._modifiable = value;

    this.update();
  }
}

export class BoxVolume extends Volume {
  constructor(args = {}) {
    super(args);

    this.constructor.counter =
      this.constructor.counter === undefined ? 0 : this.constructor.counter + 1;
    if(this.constructor.counter === 0){
      this.name = 'Volume'
    }
    else{
      this.name = 'Volume ' + '(' + this.constructor.counter + ')';
    }
    
    this.type = 'BoxVolume';

    let boxGeometry = new THREE.BoxGeometry(1, 1, 1);
    boxGeometry.computeBoundingBox();

    const boxFrame = new Float32Array([
      // bottom
      -0.5, -0.5, 0.5, 0.5, -0.5, 0.5,
      0.5, -0.5, 0.5, 0.5, -0.5, -0.5,
      0.5, -0.5, -0.5, -0.5, -0.5, -0.5,
      -0.5, -0.5, -0.5, -0.5, -0.5, 0.5,
      // top
      -0.5, 0.5, 0.5, 0.5, 0.5, 0.5,
      0.5, 0.5, 0.5, 0.5, 0.5, -0.5,
      0.5, 0.5, -0.5, -0.5, 0.5, -0.5,
      -0.5, 0.5, -0.5, -0.5, 0.5, 0.5,
      // sides
      -0.5, -0.5, 0.5, -0.5, 0.5, 0.5,
      0.5, -0.5, 0.5, 0.5, 0.5, 0.5,
      0.5, -0.5, -0.5, 0.5, 0.5, -0.5,
      -0.5, -0.5, -0.5, -0.5, 0.5, -0.5,
    ]);
    const boxFrameGeometry = new THREE.BufferGeometry();
    boxFrameGeometry.setAttribute('position',
      new Float32BufferAttribute(boxFrame, 3));

    this.material = new THREE.MeshBasicMaterial({
      color: 0x00ff00,
      transparent: true,
      opacity: 0.3,
      depthTest: true,
      depthWrite: false,
    });
    this.box = new THREE.Mesh(boxGeometry, this.material);
    this.box.geometry.computeBoundingBox();
    this.boundingBox = this.box.geometry.boundingBox;
    this.add(this.box);

    this.frame = new THREE.LineSegments(
      boxFrameGeometry,
      new THREE.LineBasicMaterial({ color: 0x000000 })
    );
    // this.frame.mode = THREE.Lines;
    this.add(this.frame);

    this.update();
  }

  update() {
    this.boundingBox = this.box.geometry.boundingBox;
    this.boundingSphere = this.boundingBox.getBoundingSphere(
      new THREE.Sphere()
    );

    if (this._clip) {
      this.box.visible = false;
      this.label.visible = false;
    } else {
      this.box.visible = true;
      this.label.visible = this.showVolumeLabel;
    }
  }

  raycast(raycaster, intersects) {
    let is = [];
    this.box.raycast(raycaster, is);

    if (is.length > 0) {
      let I = is[0];
      intersects.push({
        distance: I.distance,
        object: this,
        point: I.point.clone(),
      });
    }
  }

  getVolume() {
    return Math.abs(this.scale.x * this.scale.y * this.scale.z);
  }
}

export class SphereVolume extends Volume {
  constructor(args = {}) {
    super(args);

    this.constructor.counter =
      this.constructor.counter === undefined ? 0 : this.constructor.counter + 1;
    this.name = 'sphere_' + this.constructor.counter;
    this.type = 'SphereVolume';

    let sphereGeometry = new THREE.SphereGeometry(1, 32, 32);
    sphereGeometry.computeBoundingBox();

    this.material = new THREE.MeshBasicMaterial({
      color: 0x00ff00,
      transparent: true,
      opacity: 0.3,
      depthTest: true,
      depthWrite: false,
    });
    this.sphere = new THREE.Mesh(sphereGeometry, this.material);
    this.sphere.visible = false;
    this.sphere.geometry.computeBoundingBox();
    this.boundingBox = this.sphere.geometry.boundingBox;
    this.add(this.sphere);

    this.label.visible = false;

    const steps = 64;
    const uSegments = 8;
    const vSegments = 5;
    const verts = [];

    // pionowe „południki”
    for (let u = 0; u < uSegments; u++) {
      const alpha = (u / uSegments) * Math.PI * 2;
      const dirx = Math.cos(alpha);
      const diry = Math.sin(alpha);

      for (let i = 0; i <= steps; i++) {
        const v = (i / steps) * Math.PI * 2;
        const vN = v + (2 * Math.PI) / steps;

        const h = Math.sin(v);
        const hN = Math.sin(vN);
        const xy = Math.cos(v);
        const xyN = Math.cos(vN);

        verts.push(
          dirx * xy, diry * xy, h,
          dirx * xyN, diry * xyN, hN,
        );
      }
    }

    // poziome „równoleżniki”
    for (let vSeg = 0; vSeg <= vSegments + 1; vSeg++) {
      const uh = vSeg / (vSegments + 1);
      const h = Math.sin((1 - uh) * (-Math.PI / 2) + uh * (Math.PI / 2));
      const xyA = Math.sqrt(1 - h * h);

      for (let i = 0; i <= steps; i++) {
        const u = (i / steps) * Math.PI * 2;
        const uN = u + (2 * Math.PI) / steps;

        const dx = Math.cos(u);
        const dy = Math.sin(u);
        const dxN = Math.cos(uN);
        const dyN = Math.sin(uN);

        verts.push(
          dx * xyA, dy * xyA, h,
          dxN * xyA, dyN * xyA, h,
        );
      }
    }

    const frameGeometry = new THREE.BufferGeometry();
    frameGeometry.setAttribute('position',
      new Float32BufferAttribute(verts, 3));

    this.frame = new THREE.LineSegments(
      frameGeometry,
      new THREE.LineBasicMaterial({ color: 0x000000 })
    );
    this.add(this.frame);

    let frameMaterial = new THREE.MeshBasicMaterial({
      wireframe: true,
      color: 0x000000,
    });
    this.frame = new THREE.Mesh(sphereGeometry, frameMaterial);
    //this.add(this.frame);

    //this.frame = new THREE.LineSegments(boxFrameGeometry, new THREE.LineBasicMaterial({color: 0x000000}));
    // this.frame.mode = THREE.Lines;
    //this.add(this.frame);

    this.update();
  }

  update() {
    this.boundingBox = this.sphere.geometry.boundingBox;
    this.boundingSphere = this.boundingBox.getBoundingSphere(
      new THREE.Sphere()
    );

    //if (this._clip) {
    //	this.sphere.visible = false;
    //	this.label.visible = false;
    //} else {
    //	this.sphere.visible = true;
    //	this.label.visible = this.showVolumeLabel;
    //}
  }

  raycast(raycaster, intersects) {
    let is = [];
    this.sphere.raycast(raycaster, is);

    if (is.length > 0) {
      let I = is[0];
      intersects.push({
        distance: I.distance,
        object: this,
        point: I.point.clone(),
      });
    }
  }

  // see https://en.wikipedia.org/wiki/Ellipsoid#Volume
  getVolume() {
    return (4 / 3) * Math.PI * this.scale.x * this.scale.y * this.scale.z;
  }
}
